import {
  Accessor,
  Component,
  createContext,
  createEffect,
  createMemo,
  createResource,
  createSignal,
  Setter,
  useContext,
} from "solid-js";
import {
  AuthFuncs,
  Currency,
  DataContext,
  get_data_context,
  get_items,
  get_status,
  ImageId,
  ImageInfo,
  ItemId,
  ItemSet,
  LangCode,
  PublicItem,
  Rates,
  LoginInfo,
  Login,
} from "./api";
import { createStore } from "solid-js/store";
import { CategoryId } from "./bindings/CategoryId";
import { AttrId } from "./bindings/AttrId";
import { AttrDefinition } from "./bindings/AttrDefinition";
import { Category } from "./bindings/Category";
import { ColorDefinition } from "./bindings/ColorDefinition";
import { ColorId } from "./bindings/ColorId";
import { list_to_map, map_to_object, mapMap, parse_id_map } from "./util";
import { AttrVal } from "./bindings/AttrVal";
import { AttrCounts } from "./bindings/AttrCounts";

type CartItem = {
  id: ItemId;
  quantity: number;
};

type AppProviderProps = {
  children: any;
};

type ShoppingCartContext = {
  items: CartItem[];
  totalQuantity: number;
  addToCart: (id: number) => void;
  increment: (id: number) => void;
  decrement: (id: number) => void;
  remove: (id: number) => void;
  clear: () => void;
};
type CacheContext = {
  get_item_async: (id: ItemId) => Promise<PublicItem>;
  get_item: (id: ItemId) => PublicItem | undefined;
  get_img: (id: ImageId) => ImageInfo;
  preload_items: (ids: ItemId[]) => Promise<boolean>;
  add_set: (set: ItemSet) => void;
  attr_by_name: Accessor<Map<string, AttrId>>;
  attrs: Accessor<Map<AttrId, AttrDefinition>>;
  setAttr: (id: AttrId, def: AttrDefinition) => void;
  colors: Accessor<Map<ColorId, ColorDefinition>>;
  categories: Accessor<Map<CategoryId, Category>>;
  setCategory: (id: CategoryId, cat: Category | null) => void;
  category_counts: Accessor<Map<CategoryId, number>>;
  root_categories: Accessor<CategoryId[]>;
  fx: Accessor<Rates>;
};

export type AttrMap = Map<AttrId, AttrVal>;
export type NavigationState = {
  attrs: Accessor<AttrMap>;
  setAttr: (id: AttrId, val: AttrVal) => void;
  setAttrs: (attrs: AttrMap) => void;
  category: Accessor<CategoryId>;
  setCategory: (id: CategoryId | null) => void;
  counts: Accessor<AttrCounts>;
};

export type UserContext = {
  user: Accessor<Login | null>;
  setUser: (f: Login | LoginInfo | null) => void;
  currency: Accessor<Currency | null>;
  setCurrency: Setter<Currency | null>;
  lang: Accessor<LangCode>;
  setLang: Setter<LangCode>;
};
type AppContext = {
  cart: ShoppingCartContext;
  cache: CacheContext;
  user: UserContext;
};
const AppContext = createContext({} as AppContext);

export const useApp = () => {
  return useContext(AppContext);
};

export const AppProvider: Component<AppProviderProps> = (props) => {
  const initial: CartItem[] = JSON.parse(
    localStorage.getItem("solidjs_cart") || "[]",
  );
  const [items, setItems] = createStore<CartItem[]>(initial);

  createEffect(() => {
    const data = items.map((item) => ({ ...item }));
    console.log("cart:", data);
    localStorage.setItem("solidjs_cart", JSON.stringify(data));
  });

  const addToCart = (id: number) => {
    setItems((prev) => {
      const old: CartItem | undefined = prev.find((item) => item.id == id);
      if (old === undefined) {
        return [...prev, { id, quantity: 1 }];
      } else {
        old.quantity += 1;
        return prev;
      }
    });
  };

  const increment = (id: number) => {
    setItems(
      (item) => item.id === id,
      "quantity",
      (q) => q + 1,
    );
  };

  const decrement = (id: number) => {
    if (items.find((item) => item.id === id)?.quantity === 1) return remove(id);

    setItems(
      (item) => item.id === id,
      "quantity",
      (q) => q - 1,
    );
  };

  const remove = (id: number) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
  };

  const clear = () => {
    setItems([]);
  };

  const cart = {
    items,
    addToCart,
    increment,
    decrement,
    remove,
    clear,
    get totalQuantity() {
      return items.reduce((quantity, item) => quantity + item.quantity, 0);
    },
  };

  const items_loading: Map<ItemId, ((item: PublicItem) => any)[]> = new Map();
  const item_map: Map<ItemId, PublicItem> = new Map();
  const img_map: Map<ImageId, ImageInfo> = new Map();
  function notify(id: ItemId, item: PublicItem) {
    const list = items_loading.get(id);
    items_loading.delete(id);

    if (list !== undefined) {
      for (const f of list) {
        f(item);
      }
    }
  }
  function wait_on(id: ItemId): Promise<PublicItem> {
    return new Promise((resolve, reject) => {
      const list = items_loading.get(id);
      if (list === undefined) {
        items_loading.set(id, [resolve]);
      } else {
        list.push(resolve);
      }
    });
  }
  function add_set(set: ItemSet) {
    for (const [id, img] of set.images) {
      img_map.set(id, img);
    }
    for (const [id, item] of set.items) {
      item_map.set(id, item);
      notify(id, item);
    }
  }
  async function get_item_async(id: ItemId): Promise<PublicItem> {
    const val = item_map.get(id);
    if (val !== undefined) {
      return val;
    }

    if (items_loading.has(id)) {
      return await wait_on(id);
    }

    const set = await get_items({
      id: [id],
      limit: null,
      attrs: null,
      category: null,
    });
    add_set(set);
    return set.items[0][1];
  }
  function get_item(id: ItemId): PublicItem | undefined {
    return item_map.get(id);
  }
  function get_img(id: ImageId): ImageInfo {
    return img_map.get(id);
  }
  async function preload_items(ids: ItemId[]): Promise<true> {
    const missing = ids.filter((id) => !item_map.has(id));
    if (missing.length > 0) {
      const set = await get_items({
        id: missing,
        limit: null,
        attrs: null,
        category: null,
      });
      add_set(set);
    }

    return true;
  }

  const [dataContext, { mutate }] =
    createResource<DataContext>(get_data_context);
  const attrs = createMemo(
    () => parse_id_map(dataContext()?.attributes),
    {},
    { equals: false },
  );
  const attr_by_name = () => {
    const map = attrs();
    if (map === undefined) return;
    return mapMap(map, ([id, def]) => [def.key, id]);
  };
  const categories = () => parse_id_map(dataContext()?.categories);
  const colors = () => parse_id_map(dataContext()?.colors);
  const root_categories = () => dataContext()?.root_categories;
  const category_counts = () => list_to_map(dataContext()?.category_counts);
  const fx = () => dataContext()?.fx;
  function setData(data: DataContext) {
    mutate({
      attributes: data.attributes,
      colors: data.colors,
      root_categories: data.root_categories,
      fx: data.fx,
      categories: data.categories,
      category_counts: data.category_counts,
    });
  }
  function setAttr(id: AttrId, def: AttrDefinition) {
    console.log("setAttr", id, def);
    const data = dataContext();
    data.attributes[id] = def;
    setData(data);
  }
  function setCategory(id: CategoryId, cat: Category | null) {
    const data = dataContext();
    const cats = categories();
    if (cat === null) {
      cats.delete(id);
    } else {
      cats.set(id, cat);
    }
    data.categories = map_to_object(cats);
    setData(data);
  }

  createEffect(() => console.log("attrs", attrs()));

  const cache: CacheContext = {
    get_item_async,
    get_item,
    get_img,
    preload_items,
    add_set,
    attrs,
    setAttr,
    attr_by_name,
    categories,
    setCategory,
    colors,
    root_categories,
    category_counts,
    fx,
  };
  const user = userContext();
  return (
    <AppContext.Provider value={{ cart, cache, user }}>
      {props.children}
    </AppContext.Provider>
  );
};

export function userContext(): UserContext {
  const [user, setUser2] = createSignal<Login | null>(null);
  createEffect(() => console.log("user", user()));
  const setUser = (info: Login | LoginInfo | null) => {
    if (info === null) {
      setUser2(null);
    } else {
      setUser2({f: null, ...info});
    }
  }
  get_status().then((info) => setUser(info));

  const [currency, setCurrency] = createSignal(
    JSON.parse(localStorage.getItem("currency") || "null"),
  );
  createEffect(() => {
    localStorage.setItem("currency", JSON.stringify(currency()));
  });
  const [lang, setLang] = createSignal(
    JSON.parse(localStorage.getItem("lang") || '"en"'),
  );
  createEffect(() => {
    localStorage.setItem("lang", JSON.stringify(lang()));
  });
  return {
    user,
    setUser,
    currency,
    setCurrency,
    lang,
    setLang,
  };
}
